import { get, isNumber, merge } from 'lodash';

import { isMapType } from 'constants/chart-roles';
import { VEGA_SPEC_LEGEND_SETTINGS } from 'constants/chart-spec';
import {
  DEFAULT_RADIUS_RANGE,
  GEOSPATIAL_COLOR_DEFAULTS,
} from 'constants/geospatial';

import GeoSpatialScatterRole from 'config/map-types/geospatial-scatter';
import GeoSpatialHeatmapRole from 'config/map-types/geospatial-heatmap';
import GeoSpatialChoroplethRole from 'config/map-types/geospatial-choropleth';

import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateChannelLabel } from 'utils/chart/channel-labels';

const STYLE_PROPS = {
  titleFont: 'Akzidenz',
  titleFontWeight: 'bold',
  titleFontSize: 12,
  labelFont: 'Akzidenz',
  labelSize: 11,
  direction: 'vertical',
};

const circleArea = radius => Math.pow(radius, 2) * Math.PI;

function generateTitle(channel, channels, reductions, customisations) {
  const pickedChannel = channels[channel];
  const labelOverride = get(
    customisations,
    `channels.${channel}.labelOverride.value`
  );
  return (
    labelOverride || generateChannelLabel(pickedChannel, channel, reductions)
  );
}

function generateDiscreteColorLegend(stageData, legendSpec) {
  const {
    metadata,
    environment: { channels, reductions, customisations },
  } = stageData;

  const colors = Object.values(metadata.colorMapping);
  const categories = Object.keys(metadata.colorMapping);

  legendSpec.data.push({
    name: 'colorData',
    values: categories.map(field => ({ colorMappedField: field })),
  });

  legendSpec.scales.push({
    name: 'symbolColor',
    type: 'ordinal',
    domain: { data: 'colorData', field: 'colorMappedField' },
    range: colors,
    nice: true,
  });

  legendSpec.legends.push(
    merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
      type: 'symbol',
      fill: 'symbolColor',
      title: generateTitle('color', channels, reductions, customisations),
    })
  );
}

function generateAggregateSizeLegend(stageData, legendSpec) {
  const {
    environment: { channels, reductions, customisations },
  } = stageData;

  legendSpec.scales.push({
    name: 'symbolSize',
    type: 'linear',
    domain: { data: 'documents', field: 'size' },
    range: [
      circleArea(DEFAULT_RADIUS_RANGE[0]),
      circleArea(DEFAULT_RADIUS_RANGE[1]),
    ],
    nice: true,
    zero: false,
  });

  legendSpec.legends.push({
    ...STYLE_PROPS,
    type: 'symbol',
    title: generateTitle('size', channels, reductions, customisations),
    size: 'symbolSize',
    encode: {
      labels: {
        update: {
          text: {
            signal: generateVegaLabelTextExpression(
              channels.size,
              customisations.channels.size
            ),
          },
        },
      },
    },
  });
}

function generateContinuousColorLegend(
  stageData,
  legendSpec,
  channel,
  defaultColor
) {
  const {
    environment: { channels, reductions, customisations, meta },
    metadata,
  } = stageData;

  const { color, reverse } = get(
    customisations.options.colorContinuous,
    'value',
    defaultColor
  );
  const { min, max } = get(meta, 'legends', metadata) || {};

  // if min and max are defined, use them as a literal domain. Otherwise, use the field data
  const domain =
    isNumber(min) && isNumber(max)
      ? [min, max]
      : {
          data: 'documents',
          field: 'color',
        };

  legendSpec.scales.push({
    name: 'gradientColor',
    type: 'sequential',
    domain,
    range: { scheme: color },
    reverse,
    nice: false,
    zero: false,
  });

  legendSpec.legends.push({
    ...STYLE_PROPS,
    type: 'gradient',
    title: generateTitle(channel, channels, reductions, customisations),
    fill: 'gradientColor',
    encode: {
      labels: {
        update: {
          text: {
            signal: generateVegaLabelTextExpression(
              channels[channel],
              customisations.channels[channel]
            ),
          },
        },
      },
    },
  });
}

function generateMapLegends(stageData) {
  const {
    environment: { chartType, channels },
    spec,
  } = stageData;

  if (!isMapType(chartType)) {
    return {};
  }

  const legendSpec = {
    autosize: 'pad',
    data: [{ name: 'documents' }],
    scales: [],
    legends: [],
  };

  if (
    [GeoSpatialChoroplethRole.name, GeoSpatialHeatmapRole.name].includes(
      chartType
    )
  ) {
    if (channels.color) {
      generateContinuousColorLegend(
        stageData,
        legendSpec,
        'color',
        GEOSPATIAL_COLOR_DEFAULTS[chartType]
      );
    }

    if (channels.intensity) {
      generateContinuousColorLegend(
        stageData,
        legendSpec,
        'intensity',
        GEOSPATIAL_COLOR_DEFAULTS[chartType]
      );
    }
  }

  if (chartType === GeoSpatialScatterRole.name) {
    if (channels.color) {
      generateDiscreteColorLegend(stageData, legendSpec);
    }

    if (channels.size) {
      generateAggregateSizeLegend(stageData, legendSpec);
    }
  }

  return {
    spec: {
      ...spec,
      legends: legendSpec,
    },
  };
}

export default {
  input: [
    'spec',
    'metadata.colorMapping',
    'metadata.min',
    'metadata.max',
    'environment.customisations.channels',
    'environment.customisations.options',
    'environment.channels',
    'environment.reductions',
    'environment.chartType',
    'environment.meta.legends',
  ],
  fn: generateMapLegends,
};
export { generateMapLegends, STYLE_PROPS, circleArea };
